import braintree from 'braintree-web';

export type BraintreeSession = {
  client: any;
  /**
   * Braintree's fraud signal for this device, or null — see `collectDeviceData`.
   *
   * A promise rather than a value, and that is the whole point: it is awaited once, by a
   * pay handler, and never by anything that renders.
   */
  deviceData: Promise<string | null>;
};

const sessions: Record<string, Promise<BraintreeSession>> = {};

/**
 * How long a payment waits for device data before charging without it.
 *
 * Collection starts when the page's client is built and is not read until a wallet sheet
 * has been authorized, so anything that is going to arrive arrived long ago — this is
 * only here so that a collector which never answers cannot hold up a charge the way it
 * used to hold up a button.
 */
const DEVICE_DATA_TIMEOUT = 10000;

/**
 * The Braintree client and its device data, once per page however many wallet buttons
 * ask for them — the same shape, and for the same reason, as loadGooglePayScript beside
 * it.
 *
 * Both are properties of the page rather than of a button: the client is a gateway
 * connection built from one token, and device data describes the device. Braintree's own
 * guidance is one data collector per page whose `deviceData` is reused for every
 * transaction on it, and the product page mounts four wallet components — Apple Pay and
 * Google Pay, once beside Add to Cart and again in the sticky mobile bar — so it was
 * building four clients and four collectors for one visitor.
 *
 * That redundancy was not merely wasteful, it was the bug behind "the sticky bar shows
 * Google Pay even though Apple Pay is available". See `collectDeviceData` for the
 * mechanism.
 *
 * The two are handed over together but they do not wait for each other: `deviceData` is
 * still in flight when the session resolves. Sharing one collector is what made that
 * matter — it moved the collector off one button's critical path and onto every button's,
 * and while the session awaited it a blocked fraudnet stopped taking one wallet off the
 * page and started taking all four. Nothing renders out of device data, so nothing renders
 * behind it.
 *
 * Only a *resolved* session is remembered. Memoizing a rejection would make one dropped
 * request permanent for the page, and the next button would never retry.
 */
export default function createBraintreeSession(clientToken: string): Promise<BraintreeSession> {
  if (!sessions[clientToken]) {
    sessions[clientToken] = buildSession(clientToken).catch((error) => {
      delete sessions[clientToken];

      throw error;
    });
  }

  return sessions[clientToken];
}

async function buildSession(clientToken: string): Promise<BraintreeSession> {
  const client = await braintree.client.create({ authorization: clientToken });

  // Started here so it has every chance to finish before anyone pays, and deliberately
  // not awaited: this line is what four wallet buttons are waiting on to render.
  return { client, deviceData: collectDeviceData(client) };
}

/**
 * Device data if we can get it, null if we cannot — never a reason to withhold a payment
 * button, and never a reason to make one wait.
 *
 * It is collected by PayPal's fraudnet script, which Safari's tracking prevention, iOS
 * content blockers and the ordinary ad blockers all routinely refuse to load. When that
 * happens `dataCollector.create` does not degrade: with `kount` off it has nothing else
 * to report, finds no collector instance and *rejects*.
 *
 * Rejects eventually, which is the reason for the timeout. Blocking is not the same as
 * answering "no" — a request an extension or a tracking policy swallows can sit there
 * unanswered for as long as the browser is willing to wait, and this collector spent that
 * whole time in front of the buttons. Off the render path the only thing left behind it is
 * an authorized sheet waiting to be charged, and `DEVICE_DATA_TIMEOUT` is what keeps it
 * from waiting there indefinitely.
 *
 * The wallet components caught that rejection along with everything else in their setup
 * and rendered nothing, so a blocked third-party fraud beacon silently took a payment
 * button off the page. Braintree treats device data as an optional signal — a transaction
 * without it is charged, it is simply scored with less to go on — so it must not be able
 * to veto the button. `public/gateways/braintree.php` reads the field off the payload and
 * a null is exactly the "not collected" it already tolerates.
 *
 * Worth knowing, because it is what made the symptom so strange: fraudnet caches its
 * session id the moment the *first* caller asks for it, and every later caller
 * short-circuits on that cache and resolves successfully without waiting for the script.
 * So a blocked script did not remove all four wallet buttons, it removed exactly one —
 * whichever component happened to get there first. When that was the sticky bar's Apple
 * Pay, the bar fell through to Google Pay while Apple Pay sat right there in the page
 * body, which is precisely what QA reported.
 */
async function collectDeviceData(client: any): Promise<string | null> {
  try {
    return await Promise.race([
      braintree.dataCollector.create({ client }).then((collector: any) => collector.deviceData),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), DEVICE_DATA_TIMEOUT)),
    ]);
  } catch (error) {
    // Console only: the wallet button works without it, and the visitor has no part in
    // this to be told about.
    console.error('Could not collect Braintree device data', error);

    return null;
  }
}
